<template>
  <ValidationObserver ref="form" @submit.prevent="handleSubmit">
    <form>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ tournamentId ? "Edit Tournament" : "Create Tournament" }}
          </p>
        </header>

        <section class="modal-card-body has-text-left">
          <div class="is-flex is-justify-content-space-between">
            <ValidationProvider
              rules="required|regex:^[a-zA-Z0-9].*"
              v-slot="{ errors }"
              name="Tournament name"
              style="width: 100%"
            >
              <b-field
                label="Name"
                label-position="inside"
                :type="errors.length ? 'is-danger' : ''"
                :message="
                  errors.length
                    ? 'Tournament name must start with an alphanumeric character'
                    : ''
                "
              >
                <b-input
                  v-model="formProps.name"
                  placeholder="Tournament name"
                ></b-input>
              </b-field>
            </ValidationProvider>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <ValidationProvider
              rules="required"
              v-slot="{ errors }"
              name="Start Time"
            >
              <b-field
                label="Start Time"
                label-position="inside"
                :type="errors.length ? 'is-danger' : ''"
                :message="errors.length ? errors[0] : ''"
              >
                <b-datetimepicker
                  v-model="formProps.startTime"
                  placeholder="Type or select a date..."
                  editable
                  :disabled="hasStarted"
                  icon="calendar"
                  :timepicker="{ incrementMinutes: 5 }"
                  append-to-body
                >
                  <template #left>
                    <b-button
                      label="Now"
                      type="is-primary"
                      icon-left="clock"
                      @click="formProps.startTime = new Date()"
                    />
                  </template>

                  <template #right>
                    <b-button
                      label="Clear"
                      type="is-danger"
                      icon-left="eraser"
                      outlined
                      @click="formProps.startTime = null"
                    />
                  </template>
                </b-datetimepicker>
              </b-field>
            </ValidationProvider>

            <b-field
              label="Finish Time"
              label-position="inside"
              v-if="formProps.startTime"
            >
              <b-timepicker
                v-model="formProps.finishTime"
                placeholder="Finish Time (optional)"
                editable
                :disabled="hasFinished"
                icon="clock"
                :incrementMinutes="15"
                :min-time="formProps.startTime"
                :time-creator="
                  () => new Date(formProps.finishTime || formProps.startTime)
                "
              ></b-timepicker>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-field label="Total Rounds" style="width: 150px">
              <b-numberinput
                controls-alignment="right"
                controls-position="compact"
                :min="tournament ? tournament.currentRound : 1"
                v-model="formProps.totalRounds"
                :disabled="hasStarted"
              ></b-numberinput>
            </b-field>

            <b-field
              label="Current Round"
              style="width: 120px"
              v-if="tournamentId"
            >
              <b-numberinput
                controls-alignment="right"
                controls-position="compact"
                :min="0"
                v-model="formProps.currentRound"
                :disabled="tournament ? false : true"
              ></b-numberinput>
            </b-field>

            <b-field label="BaseTimeControl" style="width: 150px">
              <b-numberinput
                :disabled="hasStarted"
                :controls="!hasStarted"
                controls-alignment="right"
                controls-position="compact"
                min="1"
                v-model="formProps.baseTimeControl"
              ></b-numberinput>
            </b-field>

            <b-field label="Increment" style="width: 150px">
              <b-numberinput
                :disabled="hasStarted"
                :controls="false"
                controls-alignment="right"
                controls-position="compact"
                min="0"
                v-model="formProps.increment"
              ></b-numberinput>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-field label="Type" label-position="inside">
              <b-select v-model="formProps.type" :disabled="hasStarted">
                <option value="Swiss" key="typeSwiss">Swiss</option>
                <option value="Robin" key="typeRobin">RR</option>
              </b-select>
            </b-field>

            <b-field
              label="Double RR"
              label-position="inside"
              v-show="formProps.type === 'Robin'"
            >
              <b-select
                v-model="formProps.doubleRR"
                :disabled="true || hasStarted"
              >
                <option :value="false" key="doubleFalse">true</option>

                <option :value="true" key="doubleTrue">false</option>
              </b-select>
            </b-field>

            <b-field
              label="Group Size"
              label-position="inside"
              v-show="formProps.type === 'Robin'"
            >
              <b-numberinput
                disabled
                :controls="false"
                controls-alignment="right"
                controls-position="compact"
                min="0"
                v-model="formProps.groupSize"
              ></b-numberinput>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-tooltip style="width: 100%" type="is-light">
              <template v-slot:content>
                <ViewerChessboard
                  v-if="formProps.initialFen"
                  :fen="formProps.initialFen"
                />
              </template>

              <b-field label="Starting position" label-position="inside">
                <b-input
                  v-model="formProps.initialFen"
                  placeholder="initial FEN"
                ></b-input>
              </b-field>
            </b-tooltip>
          </div>
        </section>

        <footer class="modal-card-foot is-justify-content-flex-end">
          <b-button label="Cancel" @click="$emit('close')" />

          <b-button label="Submit" type="is-primary" native-type="submit" />
        </footer>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import ViewerChessboard from "@/components/variants/ViewerChessboard.vue";
import { toNativeDate } from "vue-chess-shared/utils/date-helper";

export default {
  name: "OfflineClassTournamentForm",

  components: { ViewerChessboard },

  props: ["tournament"],

  data() {
    return {
      formProps: {},
    };
  },

  computed: {
    tournamentId() {
      return this.tournament ? this.tournament.id : null;
    },

    hasStarted() {
      return this.tournament && !!this.tournament.currentRound;
    },

    hasFinished() {
      if (
        this.tournament &&
        this.tournament.currentRound >= this.tournament.totalRounds
      ) {
        return true;
      }
      return false;
    },
  },

  watch: {
    tournament: {
      deep: true,
      immediate: true,
      handler() {
        if (!this.tournament) {
          return;
        }
        this.formProps = {
          ...this.tournament,
          startTime: toNativeDate(this.tournament.startTime ?? new Date()),
        };
        if (this.tournament.finishTime) {
          this.formProps.finishTime = toNativeDate(this.tournament.finishTime);
        }
      },
    },
  },

  methods: {
    async handleSubmit() {
      const valid = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.tournamentId ? this.updateTournament() : this.createTournament();
    },

    async createTournament() {
      this.$emit("onCreate", this.formProps);
    },

    async updateTournament() {
      this.$emit("onUpdate", this.tournamentId, this.formProps);
    },
  },
};
</script>

<style scoped></style>
